import { ListIcon } from '@c/icons';
import ListingFilterControls from '@c/filters/ListingFilterControls';
import ButtonToggle from '@ui/ButtonToggle';
import Select from '@ui/Select';
import type { PageSideFilters, TopFilters } from '@util/hooks/useProductsPage';
import { ButtonToggleType } from '@util/types/buttontoggle';
import {
  ArrowUpDown,
  ChevronDown,
  Grid2X2Icon,
  SlidersHorizontal,
} from 'lucide-react';
import type { Condition, ProductSortOption } from 'models/product';
import type { SellerType } from 'models/shared';
import type { DrawerPage } from './VehicleFilterDrawer';
import { SELLER_TYPE_OPTIONS } from './VehicleFilterSidebar';

export const sortingOptions: {
  label: string;
  value: ProductSortOption;
  id: string;
}[] = [
  {
    label: 'Newest Listings',
    value: 'newest',
    id: 'newest',
  },
  {
    label: 'Price: Low to High',
    value: 'priceAsc',
    id: 'priceAsc',
  },
  {
    label: 'Price: High to Low',
    value: 'priceDesc',
    id: 'priceDesc',
  },
];

interface VehicleForSaleControlsProps {
  topFilters: TopFilters;
  onTopFiltersChange: (topFilters: TopFilters) => void;
  sideFilters: PageSideFilters;
  onSideFiltersChange: (sideFilters: PageSideFilters) => void;
  /** Opens the mobile drawer. Pass `page` to deep-link to a specific filter. */
  onOpenDrawer: (page?: DrawerPage) => void;
}

const MOBILE_PILLS: {
  key: string;
  label: string;
  page?: DrawerPage;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
}[] = [
  {
    key: 'filter',
    label: 'Filter',
    leadingIcon: <SlidersHorizontal className="h-[1.8rem] w-[1.8rem]" />,
  },
  {
    key: 'sort',
    label: 'Sort',
    page: 'sort',
    leadingIcon: <ArrowUpDown className="h-[1.8rem] w-[1.8rem]" />,
  },
  {
    key: 'sellerType',
    label: 'Seller type',
    page: 'sellerType',
    trailingIcon: <ChevronDown className="h-[1.8rem] w-[1.8rem]" />,
  },
  {
    key: 'condition',
    label: 'Condition',
    page: 'condition',
    trailingIcon: <ChevronDown className="h-[1.8rem] w-[1.8rem]" />,
  },
];

// The listing page controls: condition tabs, seller-type select, sort select,
// grid/list view toggle, and the mobile-only "Filter" button.
const VehicleForSaleControls = ({
  topFilters,
  onTopFiltersChange,
  sideFilters,
  onSideFiltersChange,
  onOpenDrawer,
}: VehicleForSaleControlsProps) => {
  const currentCondition = !sideFilters.bikeConditions?.length
    ? 'all'
    : sideFilters.bikeConditions.includes('New')
      ? 'new'
      : 'used';

  const handleConditionChange = (value: string) => {
    const bikeConditions: Condition[] =
      value === 'all' ? [] : value === 'new' ? ['New'] : ['Used'];
    onSideFiltersChange({ ...sideFilters, bikeConditions });
  };

  const handleSellerTypeChange = (value: string) => {
    onSideFiltersChange({
      ...sideFilters,
      sellerType: value ? [value as SellerType] : [],
    });
  };

  // Grid/list view toggle — the same ButtonToggle + icons the search page uses.
  const viewToggles: ButtonToggleType[] = [
    {
      key: 'grid',
      icon: <Grid2X2Icon className="h-[2.4rem] w-[2.4rem]" />,
      onClick: () => onTopFiltersChange({ ...topFilters, view: 'grid' }),
      selected: topFilters.view === 'grid',
      ariaLabel: 'Grid view',
    },
    {
      key: 'list',
      icon: <ListIcon />,
      onClick: () => onTopFiltersChange({ ...topFilters, view: 'list' }),
      selected: topFilters.view === 'list',
      ariaLabel: 'List view',
    },
  ];

  return (
    <div className="flex w-full flex-col gap-[1.6rem] lg:flex-row lg:items-center lg:justify-between">
      <div className="-mx-4 flex items-center gap-[0.8rem] overflow-x-auto px-4 scrollbar-none lg:hidden">
        {MOBILE_PILLS.map((pill) => (
          <button
            key={pill.key}
            type="button"
            onClick={() => onOpenDrawer(pill.page)}
            className="flex h-[4rem] shrink-0 items-center gap-[0.8rem] rounded-[0.8rem] bg-[#F1F1F1] pl-[1.4rem] pr-[1.8rem] text-[1.8rem] font-semibold text-brand-black"
          >
            {pill.leadingIcon}
            <span>{pill.label}</span>
            {pill.trailingIcon}
          </button>
        ))}
      </div>

      <div className="hidden lg:block">
        <ListingFilterControls
          condition={currentCondition}
          onConditionChange={handleConditionChange}
          sellerEmptyLabel="Any seller"
          sellerValue={sideFilters.sellerType?.[0] ?? ''}
          sellerOptions={SELLER_TYPE_OPTIONS.map((opt) => ({
            id: opt.key,
            label: opt.label,
            value: opt.key,
          }))}
          onSellerChange={handleSellerTypeChange}
        />
      </div>

      <div className="hidden w-full flex-col justify-between gap-[1.6rem] sm:flex-row sm:items-center lg:flex lg:w-auto lg:justify-end">
        <div className="flex items-center gap-[1.6rem] whitespace-nowrap">
          <Select
            options={sortingOptions}
            allowEmpty={false}
            value={topFilters.sort}
            onChange={({ value }) => {
              onTopFiltersChange({
                ...topFilters,
                sort: value as ProductSortOption,
              });
            }}
            className="!h-[4.5rem] min-w-[18rem] !rounded-[1.5rem] !border-none !bg-[#F1F1F1]"
            rounded
          />
        </div>
        <ButtonToggle buttons={viewToggles} />
      </div>
    </div>
  );
};

export default VehicleForSaleControls;
